// Service-worker registration for the installable PWA.
// The SW itself lives in /public/sw.js (hand-written, no build plugin):
// it caches the app shell and serves it as an offline fallback for navigations,
// and (since 29/09/2026) shows Web Push notifications with the app closed.

import { api } from './api.js';

// ── התקנת האפליקציה — אלי 29/09/2026 ────────────────────────────────────────
// „לא לכולם הפלאפון שואל אם להתקין אפליקציה." Chrome decides on its own
// whether to show its install bubble, and iOS never does. So the menu offers
// it explicitly: on Chromium we keep the `beforeinstallprompt` event and fire
// it from a click; on iOS we show the three Share-sheet steps.

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let installedNow = false;
const listeners = new Set<() => void>();
const emit = (): void => listeners.forEach((l) => l());

/** For useSyncExternalStore: re-render when install availability changes. */
export function subscribeInstall(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** true when Chromium handed us an install prompt we can fire. */
export function canPromptInstall(): boolean {
  return deferredPrompt !== null;
}

/** Fire the browser's own install dialog. Resolves to whether it was accepted. */
export async function promptInstall(): Promise<boolean> {
  const ev = deferredPrompt;
  if (!ev) return false;
  // One event is good for one prompt.
  deferredPrompt = null;
  emit();
  await ev.prompt();
  const { outcome } = await ev.userChoice;
  return outcome === 'accepted';
}

/** Running as the installed app (home-screen icon), not in a browser tab. */
export function isStandalone(): boolean {
  if (installedNow) return true;
  if (typeof window === 'undefined') return false;
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return iosStandalone || window.matchMedia?.('(display-mode: standalone)').matches === true;
}

export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  // iPadOS 13+ says "Macintosh"; the touch points give it away.
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

/** Safari itself — the only iOS browser that can add a web app to the home screen reliably. */
export function isIOSSafari(): boolean {
  if (!isIOS()) return false;
  return !/CriOS|FxiOS|EdgiOS|OPiOS|OPT\/|GSA\/|YaBrowser|DuckDuckGo|FBAN|FBAV|Instagram|Line\/|WhatsApp/.test(navigator.userAgent);
}

export function isMobile(): boolean {
  if (typeof navigator === 'undefined') return false;
  return isIOS() || /Android|Mobi/.test(navigator.userAgent);
}

export function registerServiceWorker(): void {
  if (typeof window === 'undefined') return;
  // Captured before React mounts: Chrome fires this early, once per page load.
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    emit();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    installedNow = true;
    emit();
  });
  if (!('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((err) => {
      console.warn('SW registration failed:', err);
    });
  });
}

// ── התראות לפלאפון (Web Push) — אלי 29/09/2026 ─────────────────────────────
// „כל מה שמגיע לפעמון, כל משתמש מחליט." Per device: this browser subscribes
// for the signed-in person; the server pushes every bell row to it.

export function pushSupported(): boolean {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/** iOS delivers Web Push only to an app added to the home screen (16.4+). */
export function pushNeedsInstall(): boolean {
  return isIOS() && !isStandalone();
}

async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  // `ready` never settles when no worker is registered (e.g. dev) — don't hang.
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>((r) => setTimeout(() => r(null), 5000)),
  ]);
}

export async function currentPushSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const reg = await registration();
  return reg ? reg.pushManager.getSubscription() : null;
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(padded);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export type EnablePushResult = 'ok' | 'denied' | 'unsupported' | 'no-worker';

/** Ask permission (from a click), subscribe this browser, register it for the user. */
export async function enablePush(): Promise<EnablePushResult> {
  if (!pushSupported()) return 'unsupported';
  const perm = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
  if (perm !== 'granted') return 'denied';
  const reg = await registration();
  if (!reg) return 'no-worker';
  const { publicKey } = await api<{ publicKey: string }>('/api/notifications/push/public-key');
  let sub = await reg.pushManager.getSubscription();
  // A subscription made with another server key cannot be reused.
  const key = sub?.options.applicationServerKey;
  if (sub && key) {
    const have = btoa(String.fromCharCode(...new Uint8Array(key))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    if (have !== publicKey) { await sub.unsubscribe(); sub = null; }
  }
  sub ??= await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
  });
  const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
  await api('/api/notifications/push/subscribe', { method: 'POST', body: { endpoint: json.endpoint, keys: json.keys } });
  return 'ok';
}

/** Stop pushes to this browser: drop it on the server and in the browser. */
export async function disablePush(): Promise<void> {
  const sub = await currentPushSubscription();
  if (!sub) return;
  try {
    await api('/api/notifications/push/subscribe', { method: 'DELETE', body: { endpoint: sub.endpoint } });
  } finally {
    await sub.unsubscribe().catch(() => undefined);
  }
}
